import { Alert, AlertIcon, AlertTitle, Box, Button, Divider, Flex, FormControl, Grid, IconButton, Image, Input, Select, Text, Textarea, useColorMode, useToast, VStack } from '@chakra-ui/react'
import Card from 'components/Card/Card'
import CardBody from 'components/Card/CardBody'
import CardHeader from 'components/Card/CardHeader'
import { useEffect, useRef, useState } from 'react'
import { sanitizeField } from 'functions/sanitizeForm'
import { useSubmitGlucoseMonitoring } from 'hooks/useGlucoseRegister'
import InputMask from "react-input-mask";
import { FaTrash } from "react-icons/fa"

import logoHapvida from "assets/images/logo-hapvida.png"
import dateFormat, { stringToDate } from 'functions/dateFormat'
import { isAfter } from 'date-fns'

interface GlucoseValue {
    key: number,
    time: string,
    glucose_value: string,
    status: string
}

type GlucoseValueField = Exclude<keyof GlucoseValue, 'key'>

const statusOptions = [
    "Em Jejum",
    "1h Pós café",
    "Antes do almoço",
    "1h Pós almoço",
    "Antes do jantar",
    "1h Pós jantar",
    "Madrugada",
]

const emptyValue = (key: number): GlucoseValue => ({
    key,
    time: "",
    glucose_value: "",
    status: "",
})

const GlucoseMonitoring = () => {
    const params = new URLSearchParams(window.location.search);
    const cpf = params.get('cpf') || "";

    const [finished, setFinished] = useState<boolean>(false)

    const { setColorMode } = useColorMode()

    useEffect(() => {
        setColorMode("light");
    }, [])

    const toast = useToast()
    const [submitForm, { loading: isLoading }] = useSubmitGlucoseMonitoring({
        onSuccess: () => {
            setFinished(true);
        },
        onError: () => {
            toast({
                id: "submitError",
                title: `Ocorreu um erro ao cadastrar`,
                duration: 4000,
                position: "bottom",
                status: "error"
            })
        }
    });

    const today = dateFormat(new Date(), "dd/MM/yyyy");

    const [formData, setFormData] = useState({
        cpf: cpf,
        date: today,
        observation: ""
    })

    const nextKey = useRef(1)
    const [values, setValues] = useState<GlucoseValue[]>([emptyValue(0)])

    const allowDecimal = (value: string) => {
        let sanitized = value.replace(/[^0-9.,]/g, '');

        const hasDot = sanitized.includes('.');
        const hasComma = sanitized.includes(',');

        if (hasDot && hasComma) {
            const firstSeparatorIndex = Math.min(
                sanitized.indexOf('.'),
                sanitized.indexOf(',')
            );

            sanitized =
                sanitized.slice(0, firstSeparatorIndex + 1) +
                sanitized
                    .slice(firstSeparatorIndex + 1)
                    .replace(/[.,]/g, '');
        }

        return sanitized;
    };

    const isFilled = (value?: string | null) => value != null && value.trim() != ""

    const isValidTime = (time: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(time)

    const addValue = () => {
        setValues(prev => [...prev, emptyValue(nextKey.current++)])
    }

    const removeValue = (key: number) => {
        setValues(prev => prev.length > 1 ? prev.filter(v => v.key !== key) : prev)
    }

    const updateValue = (key: number, field: GlucoseValueField, value: string) => {
        setValues(prev => prev.map(v => v.key === key ? { ...v, [field]: value } : v))
    }

    const showWarning = (id: string, title: string) => {
        toast({
            id,
            title,
            duration: 4000,
            position: "bottom",
            status: "warning"
        })
    }

    const onSubmit = () => {
        const sanitizedCpf = sanitizeField(formData.cpf);

        const allValuesFilled = values.every(v =>
            isFilled(v.time) && isFilled(v.glucose_value) && isFilled(v.status)
        )

        if (!isFilled(sanitizedCpf) || formData.date.length != 10 || !allValuesFilled) {
            showWarning("requiredFields", "Por favor, preencha todos os campos obrigatórios.");
            return;
        }

        if (!values.every(v => isValidTime(v.time))) {
            showWarning("invalidTime", "Informe um horário válido (HH:MM).");
            return;
        }

        if (isAfter(stringToDate(formData.date), new Date())) {
            showWarning("invalidDate", "Data informada não pode ser maior que hoje");
            return;
        }

        const variables = {
            input: {
                ...formData,
                cpf: sanitizedCpf,
                values: values.map(({ time, glucose_value, status }) => ({ time, glucose_value, status }))
            }
        }

        console.log(">>", variables);

        submitForm({ variables });
    }

    return (
        <Flex w="full" minH="100vh" justifyContent="center" alignItems="center">
            <Card maxW="420px" minH="460px">
                <CardHeader minH="80px" justifyContent="center" alignItems="center" mx="auto">
                    <VStack w="full" h="full">
                        <Image src={logoHapvida} maxW="200px" h="auto" />
                        <Text align="center">Monitoramento Glicêmico para Diabetes Gestacional</Text>
                    </VStack>
                </CardHeader>
                <CardBody mt="auto" mb="auto">
                    <Flex flexDirection="column" w="full">
                        {
                            finished ?
                                <Flex w="full" justifyContent="center" alignItems="center">
                                    <Alert
                                        status='success'
                                        variant='subtle'
                                        flexDirection='column'
                                        alignItems='center'
                                        justifyContent='center'
                                        textAlign='center'
                                        height='200px'
                                    >
                                        <AlertIcon boxSize='40px' mr={0} />
                                        <AlertTitle mt={4} mb={1} fontSize='md'>
                                            <Text>Obrigado pelo preenchimento das informações.</Text>
                                            <Text>Lembre-se sempre de acessar o mesmo link para registrar outros valores, conforme orientação.</Text>
                                        </AlertTitle>
                                    </Alert>
                                </Flex>
                                :
                                <>
                                    <Grid
                                        w="full"
                                        templateColumns="repeat(1, 1fr)"
                                        gap="16px"
                                        mb="8"
                                    >
                                        <FormControl>
                                            <Text mb={2} fontWeight="bold" fontSize="md">Data *</Text>
                                            <Input
                                                value={formData.date}
                                                onChange={({ target }) => setFormData({ ...formData, date: target.value })}
                                                as={InputMask}
                                                mask={"99/99/9999"}
                                                borderRadius="8px"
                                                fontSize="md"
                                            />
                                        </FormControl>
                                        <VStack w="full" spacing={4} divider={<Divider />}>
                                            {
                                                values.map((vl) => (
                                                    <Box key={vl.key} w="full" position="relative">
                                                        {
                                                            values.length > 1 &&
                                                            <IconButton
                                                                aria-label="Remover valor"
                                                                icon={<FaTrash />}
                                                                size="sm"
                                                                colorScheme="red"
                                                                variant="ghost"
                                                                position="absolute"
                                                                top={0}
                                                                right={0}
                                                                zIndex={1}
                                                                isDisabled={isLoading}
                                                                onClick={() => removeValue(vl.key)}
                                                            />
                                                        }
                                                        <VStack w="full" spacing={4}>
                                                            <FormControl>
                                                                <Text mb={2} fontWeight="bold" fontSize="md">Horário *</Text>
                                                                <Input
                                                                    value={vl.time}
                                                                    onChange={({ target }) => updateValue(vl.key, "time", target.value)}
                                                                    as={InputMask}
                                                                    mask={"99:99"}
                                                                    borderRadius="8px"
                                                                    fontSize="md"
                                                                />
                                                            </FormControl>
                                                            <FormControl>
                                                                <Text mb={2} fontWeight="bold" fontSize="md">Valor da Glicemia (mg/dL) *</Text>
                                                                <Input
                                                                    value={vl.glucose_value}
                                                                    onChange={({ target }) => updateValue(vl.key, "glucose_value", allowDecimal(target.value))}
                                                                    inputMode="decimal"
                                                                    borderRadius="8px"
                                                                    fontSize="md"
                                                                />
                                                            </FormControl>
                                                            <FormControl>
                                                                <Text mb={2} fontWeight="bold" fontSize="md">Situação *</Text>
                                                                <Select
                                                                    value={vl.status}
                                                                    onChange={({ target }) => updateValue(vl.key, "status", target.value)}
                                                                    placeholder="Selecione uma opção..."
                                                                >
                                                                    {
                                                                        statusOptions.map(option => (
                                                                            <option key={option} value={option}>{option}</option>
                                                                        ))
                                                                    }
                                                                </Select>
                                                            </FormControl>
                                                        </VStack>
                                                    </Box>
                                                ))
                                            }
                                        </VStack>
                                        <Button
                                            disabled={isLoading}
                                            w="full"
                                            colorScheme="blue"
                                            variant="outline"
                                            onClick={addValue}
                                        >Informar outro valor</Button>
                                        <FormControl>
                                            <Text mb={2} fontWeight="bold" fontSize="md">Observação</Text>
                                            <Textarea
                                                value={formData.observation}
                                                onChange={({ target }) => setFormData({ ...formData, observation: target.value })}
                                                borderRadius="8px"
                                                fontSize="md"
                                                minH={100}
                                                placeholder="Registre aqui observações relacionadas ao uso de medicações, dieta, ganho de peso ou demais informações que julgar pertinente repassar para a equipe de saúde sobre a data informada, tais como: alimentação, uso de alguma medicação, ou qualquer outro dado."
                                            />
                                        </FormControl>
                                        <Text fontSize="sm">* Campos obrigatórios</Text>
                                    </Grid>
                                    <Flex w="full" mb="2" direction="column" gap={2}>
                                        <Button
                                            isLoading={isLoading}
                                            disabled={isLoading}
                                            w="full"
                                            colorScheme="blue"
                                            onClick={onSubmit}
                                        >Salvar e Finalizar</Button>
                                    </Flex>
                                </>
                        }
                    </Flex>
                </CardBody>
            </Card>
        </Flex>
    )
}

export default GlucoseMonitoring
